import React from 'react';
import { Phone, Mail, MapPin, Send, Facebook } from 'lucide-react';
import Layout from '../components/Layout';
import { COMPANY_INFO, ASSETS } from '../data';

const Kontakt = () => {
  return (
    <Layout>
      <section className="pt-56 pb-24 bg-slate-900 min-h-[50vh] flex items-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="text-center">
            <h1 className="text-[2rem] md:text-7xl font-bold text-white tracking-tighter mb-6">KONTAKT OS</h1>
            <p className="text-amber-500 font-bold tracking-[0.3em] uppercase">Vi er klar til at hjælpe med dit næste projekt</p>
          </div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Top part: Image left, Contact info right */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 mb-24 items-start">
            
            {/* Simon Image */}
            <div className="relative aspect-[4/5] md:aspect-[3/2] lg:aspect-[4/5] overflow-hidden bg-slate-100 shadow-2xl">
              <img 
                src={ASSETS.simon} 
                alt="Simon - Jensen & Sørensen ApS" 
                className="w-full h-full object-cover"
              />
              <div className="absolute bottom-0 left-0 p-8 w-full bg-gradient-to-t from-slate-900/90 to-transparent text-white">
                <p className="text-amber-500 font-bold text-xs tracking-widest uppercase mb-1">Direkte kontakt</p>
                <h3 className="text-3xl font-bold">Simon Sørensen</h3>
                <p className="text-white/70">Medejer & Tømrermester</p>
              </div>
            </div>

            {/* Contact Boxes */}
            <div className="flex flex-col gap-8">
              <div className="p-10 bg-slate-50 border border-slate-100 flex items-start gap-6">
                <Phone className="text-amber-500 shrink-0 mt-1" size={32} />
                <div>
                  <h4 className="font-bold text-xl text-slate-900 mb-2">Ring direkte</h4>
                  <div className="space-y-1 text-slate-600">
                    <p>Simon: {COMPANY_INFO.phone.simon}</p>
                    <p>Mark: {COMPANY_INFO.phone.mark}</p>
                  </div>
                </div>
              </div>
              
              <div className="p-10 bg-slate-50 border border-slate-100 flex items-start gap-6">
                <Mail className="text-amber-500 shrink-0 mt-1" size={32} />
                <div>
                  <h4 className="font-bold text-xl text-slate-900 mb-2">Skriv en mail</h4>
                  <p className="text-slate-600 break-all">{COMPANY_INFO.email}</p>
                </div>
              </div>

              <div className="p-10 bg-slate-50 border border-slate-100 flex items-start gap-6">
                <Facebook className="text-amber-500 shrink-0 mt-1" size={32} />
                <div>
                  <h4 className="font-bold text-xl text-slate-900 mb-2">Facebook</h4>
                  <p className="text-slate-600 mb-4">Følg vores arbejde og se nyeste projekter.</p>
                  <a 
                    href={COMPANY_INFO.facebook} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-amber-500 font-bold hover:underline"
                  >
                    Besøg vores side →
                  </a>
                </div>
              </div>
            </div>

          </div>

          {/* Bottom part: Webform */}
          <div className="max-w-4xl mx-auto">
            <div className="bg-slate-900 p-10 md:p-16 shadow-2xl">
              <h3 className="text-3xl font-bold text-white mb-8 text-center">Send os en besked</h3>
              <form 
                className="space-y-6"
                action="https://api.dev.auxo.dk/webform/submit"
                method="POST"
              >
                {/* Honeypot */}
                <div className="sr-only absolute -z-10">
                  <label htmlFor="website_url">Honeypot</label>
                  <input type="text" id="website_url" name="website_url" tabIndex="-1" autoComplete="off" />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Navn</label>
                    <input 
                      type="text" 
                      name="name" 
                      required
                      className="w-full bg-white/5 border border-white/10 p-4 text-white focus:outline-none focus:border-amber-500 transition-colors"
                      placeholder="Dit fulde navn"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Telefon</label>
                    <input 
                      type="tel" 
                      name="phone" 
                      required
                      className="w-full bg-white/5 border border-white/10 p-4 text-white focus:outline-none focus:border-amber-500 transition-colors"
                      placeholder="Dit telefonnummer"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Email</label>
                  <input 
                    type="email" 
                    name="email" 
                    required
                    className="w-full bg-white/5 border border-white/10 p-4 text-white focus:outline-none focus:border-amber-500 transition-colors"
                    placeholder="Din email adresse"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Besked</label>
                  <textarea 
                    name="message" 
                    rows="5"
                    required
                    className="w-full bg-white/5 border border-white/10 p-4 text-white focus:outline-none focus:border-amber-500 transition-colors resize-none"
                    placeholder="Fortæl kort om dit projekt..."
                  ></textarea>
                </div>

                <button 
                  type="submit"
                  className="w-full py-5 bg-amber-500 text-white font-bold hover:bg-amber-600 transition-all flex items-center justify-center gap-3 uppercase tracking-widest text-sm"
                >
                  Send forespørgsel <Send size={18} />
                </button>
              </form>
            </div>
          </div>

        </div>
      </section>
    </Layout>
  );
};

export default Kontakt;
